// Core types for the Insurance Quoting & Binding Tool
import type { PolicyResponseDto } from '../api/models/PolicyResponseDto';

export type UserRole = 'ADVISER' | 'BUSINESS_ADMIN' | 'FINANCE_ADMIN' | 'SUPER_ADMIN';

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  adviserId?: string;
  fapId?: string;
  roles?: UserRole[]; // Multiple roles support
  mfaEnabled?: boolean;
  lastLogin?: string;
  status?: 'active' | 'inactive';
  adviserRole?: AdviserRole | null;
}

export type ClientType = 'individual' | 'business';
export type ClientStatus = 'in-progress' | 'bound' | 'lapsed' | 'renewal-due';
export type AddressType = 'Billing Address' | 'Branch Address' | 'Business Address' | 'Correspondence Address' | 'Home Address' | 'Other' | 'Registered Address' | 'Site Address';

export interface Address {
  id: string;
  addressType: AddressType;
  unitStreet: string;
  suburb: string;
  city: string;
  postcode: string;
  country: string;
  lookupSuggestionId?: string;
}

export interface MainContact {
  preferredName?: string;
  lastName?: string;
  email?: string;
  preferredPhone?: 'Mobile' | 'Home' | 'Work';
  mobile?: string;
  homePhone?: string;
  workPhone?: string;
  externalReference?: string;
}

export interface AdditionalInsured {
  id: string;
  title?: string;
  firstName: string;
  lastName: string;
  dob: string;
  gender: 'Male' | 'Female' | 'Other';
  preferredName?: string;
  email?: string;
  preferredPhone?: 'Mobile' | 'Home' | 'Work';
  mobile?: string;
  homePhone?: string;
  workPhone?: string;
  relationship?: 'Partner/Spouse' | 'Parent' | 'Child' | 'Friend' | 'Other' | 'Policy Owner' | 'Business Partner' | 'Sibling';
}

export type AdviserLinkType = 'authorised-rep' | 'servicing-adviser';
export type CommissionRecipientType = 'adviser' | 'fap' | 'inherit';

export interface AdviserLink {
  id: string;
  linkType: AdviserLinkType;
  adviserId: string;
  adviserUserId?: string; // User ID for frontend navigation to adviser detail page
  adviserName: string;
  fapId: string;
  fapName: string;
  effectiveFrom: string;
  effectiveTo?: string; // undefined = current
  commissionRecipient: CommissionRecipientType;
  changedBy: string;
  changedByName?: string;
  changedAt: string;
  reason?: string;
  status: 'current' | 'historical';
}

export interface Client {
  id: string;
  title?: string;
  firstName: string;
  lastName: string;
  dob?: string;
  gender?: 'Male' | 'Female' | 'Other';
  clientType: ClientType;
  status: ClientStatus;
  /**
   * Ando customer_id (set after client is synced to Ando).
   * Required to create quotes via backend `POST /quotes`.
   */
  andoCustomerId?: string;
  /**
   * Ando customer_code (optional, informational)
   */
  andoCustomerCode?: string;
  andoCustomerSyncError?: string;
  /**
   * Last successful Ando customer sync (ISO string). Changes when a background sync completes.
   */
  andoCustomerSyncedAt?: string;
  address: {
    unit?: string;
    street: string;
    suburb: string;
    postcode: string;
  };
  addresses?: Address[];
  phone: string;
  email: string;
  assignedAdviser?: string;
  assignedAdviserName: string;
  adviserLinks?: AdviserLink[]; // New: adviser assignment history
  createdAt: string;
  lastActivity: string;
  notes?: string;
  isVulnerable?: boolean; // Flag for vulnerable clients requiring special care
  hasMultiplePolicies?: boolean; // Flag for clients with policies at other providers
  unallocatedCash: number;
  manualUnallocatedCash?: number;
  totalUnallocatedCash?: number;
  // Organisation-specific fields
  companyRegNo?: string;
  tradingAs?: string;
  gstNumber?: string;
  mainContact?: MainContact;
  additionalInsureds?: AdditionalInsured[];
}

/**
 * Get the full name of a client
 * For INDIVIDUAL clients: returns "firstName lastName"
 * For BUSINESS clients: returns firstName (which contains the business name)
 */
export function getClientName(client: Client): string {
  if (client.clientType === 'business') {
    return client.firstName; // Business name is stored in firstName
  }
  return `${client.firstName} ${client.lastName}`.trim();
}

export type QuoteType = 'house' | 'contents' | 'vehicle';
export type QuoteStatus =
  | 'DRAFT'
  | 'QUOTED'
  | 'REFERRAL_SUBMITTED'
  | 'REFERRAL_ACTION_REQUIRED'
  | 'CANCELLED'
  | 'BOUND'
  | 'DECLINED'
  | 'COMPLETE'
  | 'referral-approved'
  | 'submitted-referral'
  | 'in-progress'
  | 'quoted'
  | 'in-force'
  | 'referral'
  | 'referred'
  | 'declined';
export type RiskType = 'house' | 'contents' | 'vehicle';
export type RiskStatus = 'DRAFT' | 'QUOTED' | 'COMPLETE' | 'REFERRED' | 'REFERRAL_REQUIRED' | 'REFERRAL_SUBMITTED';
export type RenewalStatus = 'Not Sent to Client' | 'Sent to Client' | 'Risks Changed';
export type ConstructionTypeEnum = 'ARTIFICIAL WEATHERBOARD/PLANK CLADDING' | 'BLOCKWORK' | 'BRICK' | 'BRICK VENEER' | 'CONCRETE' | 'DOUBLE BRICK' | 'FIBRE CEMENT' | 'METAL' | 'MIX.MATERIAL' | 'MUD BRICK' | 'OTHER' | 'ROCKCOTE/EPS' | 'ROUGHCAST' | 'SHEET CLADDING' | 'SOLID BRICK' | 'STONE' | 'STONE VENEER' | 'STUCCO' | 'TIMBER/WEATHERBOARD';
export type HouseOccupancyEnum =
  | 'BOARDING HOUSE'
  | 'EMPLOYEE'
  | 'HOLIDAY HOME FOR RENTING'
  | 'OTHER'
  | 'OWNER OCCUPIED & CASUAL LETTING'
  | 'OWNER OCCUPIED & SHARED WITH 3 OR MORE FLATMATES'
  | 'OWNER OCCUPIED & SHARED WITH LESS THAN 3'
  | 'OWNER OCCUPIED'
  | 'OWNERS HOLIDAY HOME & CASUAL LETTING'
  | 'OWNERS HOLIDAY HOME'
  | 'RELATIVE WITH NO TENANCY AGREEMENT'
  | 'RENTAL PROPERTY – PROFESSIONALLY MANAGED'
  | 'RENTAL PROPERTY'
  | 'SOCIAL HOUSING'
  | 'UNOCCUPIED';
export const MORTGAGE_OPTIONS = [
  { value: 'ANZ BANK NEW ZEALAND LIMITED', label: 'ANZ Bank New Zealand Limited' },
  { value: 'ASB BANK LIMITED', label: 'ASB Bank Limited' },
  { value: 'BANK OF NEW ZEALAND LIMITED', label: 'Bank of New Zealand Limited' },
  { value: 'HEARTLAND BANK LIMITED', label: 'Heartland Bank Limited' },
  { value: 'HSBC LIMITED', label: 'HSBC Limited' },
  { value: 'KIWIBANK LIMITED', label: 'Kiwibank Limited' },
  { value: 'NO MORTGAGE', label: 'No mortgage' },
  { value: 'OTHER', label: 'Other' },
  { value: 'SOUTHLAND BUILDING SOCIETY', label: 'Southland Building Society' },
  { value: 'THE CO-OPERATIVE BANK LIMITED', label: 'The Co-operative Bank Limited' },
  { value: 'TSB BANK LIMITED', label: 'TSB Bank Limited' },
  { value: 'WESTPAC NEW ZEALAND LIMITED', label: 'Westpac New Zealand Limited' },
] as const;

export type MortgageEnum = (typeof MORTGAGE_OPTIONS)[number]['value'];

export interface UniqueFeature {
  id: string;
  type: string;
  description: string;
  unique_feature_sum_insured: number;
}

export interface SpecifiedItem {
  id: string;
  type: string;
  description: string;
  sumInsured: number;
  excess?: string; // Excess amount for the specified item
}

export interface HouseClaim {
  cost: number;
  date_of_loss: string;
  type_of_loss: string;
}

export interface ContentsClaim {
  cost: number;
  date_of_loss: string;
  type_of_loss: string;
}

export interface HouseRiskData {
  claims?: any[];

  house_location: {
    unit_number_street: string;
    suburb: string;
    city: string;
    post_code: string;
    country: string;
    address_identifier: string;
  },
  house_details: {
    cv?: number;
    iv?: number;
    lv?: number;
    deck?: boolean;
    pool?: boolean;
    qpid?: string;
    levels?: number;
    heritage?: boolean;
    mortgage?: MortgageEnum;
    roof_type?: 'CONCRETE' | 'FIBRECEMENT' | 'METAL (IRON/COLOURSTEEL/ALUMINIUM)' | 'TILE' | 'OTHER';
    floor_area?: number;
    year_built?: number;
    slope_score?: string;
    home_rewired?: boolean;
    tennis_court?: boolean;
    body_corporate?: boolean;
    used_for_business?: boolean;
    roof_condition?: 'AVERAGE' | 'GOOD' | 'POOR';
    wall_condition?: string;
    house_occupancy?: HouseOccupancyEnum;
    retaining_walls?: boolean;
    well_maintained?: boolean;
    disaster_details?: string;
    hazards_on_title?: boolean;
    is_house_alarmed?: boolean;
    alarm_monitored?: boolean;
    type_of_business?: string;
    construction_type?: ConstructionTypeEnum;
    other_type_of_business?: string;
    other_mortgage_details?: string;
    freestanding_garage_sqm?: number;
    any_unrepaired_eq_damage?: boolean;
    self_contained_dwellings?: number;
    detached_outbuildings_sqm?: number;
    suffered_natural_disaster?: boolean;
    construction_standard?: 'STANDARD' | 'QUALITY' | 'PRESTIGE';
    recalculate_dynamic_url?: string;
  },
  referral: {
    referral_notes: string[];
    referral_details: string[];
    referral_reasons: string[];
  },
  coverage: {
    unique_features: UniqueFeature[];
    total_sum_insured: number;
    carpet_sum_insured?: number;
    any_unique_features: boolean;
    requested_sum_insured: number;
    estimated_sum_insured?: number;
    house_optional_extensions?: {
      glass_breakage_no_excess: boolean;
      landscape_cover_included: boolean;
      other_policies_with_ando: boolean;
      landlord_extension_included: 'YES - COMPREHENSIVE' | 'YES - STANDARD' | 'NO';
    },
    lifestyle_wording_applies: boolean;
  },
  terms: {
    clauses?: {
      clause_code: string;
      clause_text: string;
    }[];
    excesses: {
      every_loss?: number;
      total_excess?: number;
      imposed_excess?: number;
      excess_under_25?: number;
      selected_excess: string;
    }[];
    sublimits?: {
      index: string;
      limit: string;
      caption: string;
    }[];
    risk_premiums?: {
      ratings: {
        annual_amount: number;
        transaction_amount: number;
        rating_section_code: string;
        rating_section_description: string;
      }[];
      riskCommission?: {
        tax_val: number;
        comm_val: number;
      };
      premium_due_net: number;
      premium_due_tax: number;
      risk_commission: string;
      premium_due_gross: number;
      risk_level_tax_fees: {
        amount: number;
        description: string;
      }[];
    };
  },
  claims_history: {
    claims_declaration_flag: boolean | undefined;
    claim_details: HouseClaim[];
  },
  quote_reference: string;
  risk_id: string;
  risk_type: string;
  risk_key?: number;
  risk_description?: string;
  risk_folder_key?: number;
  risk_status?: 'QUOTED' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED' | 'CANCELLED' | 'PENDING';
}

export interface ContentsRiskData {
  claims?: any[];
  contents_location: {
    address_identifier?: string;
    qpid?: string;
    address_x?: string;
    address_y?: string;
    city?: string;
    country?: string;
    post_code?: string;
    suburb?: string;
    unit_number_street?: string;
  },
  contents_house_details: {
    is_house_alarmed?: boolean;
    used_for_business?: boolean;
    house_occupancy:
    | "OWNER OCCUPIED"
    | "HOLIDAY HOME"
    | "TENANT"
    | "TENANT (NON-FAMILY MEMBERS)"
    | "UNOCCUPIED"
    | "HOLIDAY HOME & CASUAL LETTING"
    | "EMPLOYEE"
    | "RELATIVE"
    | "LANDLORDS CONTENTS"
    | "STORAGE - ALL CONTENTS"
    | "OWNER OCCUPIED & SHARED WITH LESS THAN 3 FLATMATES"
    | "OWNER OCCUPIED & SHARED WITH 3 OR MORE FLATMATES";
    type_of_business?:
    | "BEAUTICIAN / HAIRDRESSER"
    | "BED & BREAKFAST / AIRBNB"
    | "HOME OFFICE"
    | "LIFESTYLE FARM"
    | "OTHER";
    other_type_of_business?: string;
    suffered_natural_disaster?: boolean;
    disaster_details?: string;
    well_maintained?: boolean;
  },
  coverage: {
    total_sum_insured?: number;
    contents_sum_insured?: number;
    lifestyle_wording_applies?: boolean;
    contents_optional_extensions?: {
      excess_free_spectacles: boolean;
      other_policies_with_ando: boolean;
    },
    specified_items: {
      type:
      | "BICYCLES"
      | "CAMERAS (INCLUDING LENSES)"
      | "COLLECTIONS (STAMP, COIN, CARD OR MEDAL)"
      | "JEWELLERY - COLLECTION (INCLUDING WATCHES)"
      | "JEWELLERY - ITEM (INCLUDING WATCHES)"
      | "WORKS OF ART - COLLECTION"
      | "WORKS OF ART - ITEM";
      description: string;
      sum_insured: number;
    }[];
  },
  referral?: {
    referral_notes: string[];
    referral_details: string[];
    referral_reasons: string[];
  },
  claims_history?: {
    claims_declaration_flag?: boolean;
    claim_details: ContentsClaim[];
  },
  terms: {
    clauses?: {
      clause_code: string;
      clause_text: string;
    }[];
    excesses: {
      every_loss?: number;
      total_excess?: number;
      imposed_excess?: number;
      excess_under_25?: number;
      selected_excess: string;
    }[];
    sublimits?: {
      index: string;
      limit: string;
      caption: string;
    }[];
    risk_premiums?: {
      ratings: {
        annual_amount: number;
        transaction_amount: number;
        rating_section_code: string;
        rating_section_description: string;
      }[];
      premium_due_net: number;
      premium_due_tax: number;
      premium_due_gross: number;
      risk_level_tax_fees: {
        amount: number;
        description: string;
      }[];
    };
  }
}

export interface MainDriver {
  name: string;
  dob: string;
  gender: string; // See Gender enum: MALE, FEMALE, ANOTHER GENDER
  licenceType: string; // See LicenceType enum: FULL NZ LICENCE, RESTRICTED, LEARNER, DON'T HAVE A FULL NZ LICENCE
}

export interface AdditionalDriver {
  name: string;
  dob: string;
  gender: string; // See Gender enum: MALE, FEMALE, ANOTHER GENDER
  licenceType: string; // See LicenceType enum: FULL NZ LICENCE, RESTRICTED, LEARNER, DON'T HAVE A FULL NZ LICENCE
}

export interface VehicleClaim {
  id: string;
  driverName: string;
  claimType: string; // See ClaimType enum: AT FAULT ACCIDENT, THEFT, FIRE
  claimDate: string;
  claimCost: string;
}

export interface DrivingOffence {
  id: string;
  driverName: string;
  offenceDetails: string;
  offenceDate: string;
}

export interface VehicleRiskData {
  claims?: any[];
  vehicle_details: {
    accessories_declaration?: boolean;
    axle_configuration?: string;
    body_style?: string;
    cc_rating?: string;
    cmvsp_stolen?: string;
    engine?: string;
    fuel_type?: string;
    gvm_mass?: string;
    is_petrol_turbo?: boolean;
    make?: string;
    model?: string;
    odometer?: string;
    odometer_unit?: "Km" | "Miles";
    origin?: string;
    other_finance?: string;
    power?: string;
    redbook_code: string;
    registration_number?: string;
    type?:
    | "AGRICULTURAL MACHINE"
    | "ATV"
    | "BUS"
    | "GOODS VAN/TRUCK/UTILITY"
    | "HIGH SPEED AGRICULTURAL VEHICLE"
    | "MOBILE MACHINE"
    | "MOPED"
    | "MOTOR CARAVAN"
    | "MOTORCYCLE"
    | "PASSENGER CAR/VAN"
    | "SPECIAL PURPOSE VEHICLE"
    | "STOCK"
    | "TRACTOR"
    | "TRAILER NOT DESIGNED FOR HIGHWAY USE"
    | "TRAILER/CARAVAN";
    used_for_business?: boolean;
    used_for_business_type?: boolean;
    variant?: string;
    vehicle_financed?:
    | "AA FINANCE"
    | "ANZ BANK NEW ZEALAND LIMITED"
    | "ASB BANK LIMITED"
    | "BANK OF NEW ZEALAND LIMITED"
    | "HSBC LIMITED"
    | "KIWIBANK LIMITED"
    | "MTF"
    | "NO FINANCE"
    | "OTHER"
    | "THE CO-OPERATIVE BANK LIMITED"
    | "WESTPAC NEW ZEALAND LIMITED";
    vin?: string;
    where_is_vehicle_parked_at_home?:
    | "CARPORT"
    | "DRIVEWAY"
    | "GARAGE"
    | "ON STREET"
    | "OTHER";
    year?: string;
  },
  vehicle_location: {
    address_identifier?: string;
    address_x?: string;
    address_y?: string;
    city?: string;
    country?: string;
    post_code?: string;
    suburb?: string;
    unit_number_street?: string;
  },
  driver_details: {
    driver_name?: string;
    driver_date_of_birth?: string;
    gender?: "MALE" | "FEMALE" | "ANOTHER GENDER";
    driver_licence_type?:
    | "DON'T HAVE A FULL NZ LICENCE"
    | "FULL NZ LICENCE LESS THAN 2 YEARS"
    | "FULL NZ LICENCE MORE THAN 2 YEARS";
    main_driver?: boolean;
  }[],
  coverage: {
    requested_sum_insured: number;
    type_of_cover: "COMPREHENSIVE" | "THIRD PARTY ONLY" | "THIRD PARTY, FIRE & THEFT";
    accessories_declaration?: boolean;
    offended_driving_licence?: boolean;
    modification_details: string;
    lifestyle_wording_applies: boolean;
    vehicle_optional_extensions: {
      other_policies_with_ando?: boolean;
      rental_car?: boolean;
      roadside_assistance?: boolean;
      kilometers_driven_per_year?:
      | "LESS THAN 5,000"
      | "5,000 - 10,000"
      | "10,000 - 20,000"
      | "MORE THAN 20,000";
      exclude_under_25_years?: boolean;
      under_25_years_drivers_present?: boolean;
      windscreen?: boolean;
    };
  },
  driving_licence_offence: {
    driver_name: string;
    offence_details:
    | "ALCOHOL/DRUG RELATED CHARGES"
    | "CARELESS/DANGEROUS/RECKLESS DRIVING"
    | "DRIVING WITHOUT A LICENCE/OUTSIDE LICENCE TERMS"
    | "SPEEDING"
    | "OTHER";
    offence_date: string;
  }[],
  referral: {
    referral_notes: string[];
    referral_details: string[];
    referral_reasons: string[];
  },
  claims_history: {
    claims_declaration_flag?: boolean;
    claim_details: {
      cost: number;
      driver_name: string;
      date_of_loss: string;
      type_of_loss:
      | "AT FAULT ACCIDENT"
      | "DAMAGED WHILE PARKED"
      | "FIRE"
      | "FLOOD"
      | "MALICIOUS DAMAGE"
      | "NOT AT FAULT ACCIDENT"
      | "OTHER"
      | "STORM"
      | "THEFT";
    }[];
  },
  terms: {
    clauses?: {
      clause_code: string;
      clause_text: string;
    }[];
    excesses: {
      every_loss?: number;
      total_excess?: number;
      imposed_excess?: number;
      excess_under_25?: number;
      selected_excess: string;
    }[];
    sublimits?: {
      index: string;
      limit: string;
      caption: string;
    }[];
    risk_premiums?: {
      ratings: {
        annual_amount: number;
        transaction_amount: number;
        rating_section_code: string;
        rating_section_description: string;
      }[];
      premium_due_net: number;
      premium_due_tax: number;
      premium_due_gross: number;
      risk_level_tax_fees: {
        amount: number;
        description: string;
      }[];
    };
  }

}

export interface Risk {
  id: string;
  quoteId: string;
  type: RiskType;
  status?: RiskStatus | 'in-force';
  data: HouseRiskData | ContentsRiskData | VehicleRiskData | any;
  createdAt: string;
  updatedAt: string;
  sumInsured?: number;
  chargeExGst?: number;
  description?: string;
  andoRiskKey?: number;
  // Pricing fields
  premium?: number;
  excess?: number;
  startDate?: string;
  renewalDate?: string;
  riskId?: string; // Legacy/Alias support
}

export interface Commission {
  riskNumber: number;
  riskType: string;
  commission: number;
}

export interface QuoteFees {
  adminFee: number;
  brokerFee: number;
}

export interface QuotePricing {
  status: QuoteStatus;
  risks: Risk[];
  fees: QuoteFees;
  basePremium?: number;
  totalPremium?: number;
  riskPremiums?: PolicyRiskPremium[];
  message?: string;
  calculatedAt?: string;
}

// eOffer types
export type EOfferStatus = 'draft' | 'sent' | 'viewed' | 'partially-completed' | 'accepted' | 'declined' | 'expired';

export type PaymentFrequency = 'fortnightly' | 'monthly' | 'annual';

export interface InstalmentDetails {
  frequency: PaymentFrequency;
  premiumFundingQuoteId?: string;
  // From premium funding API FormattedResponse
  firstInstalment: string;
  firstPaymentDueDate: string;
  numberOfPayments: number;
  regularInstalment: string;
  regularInstalmentLastDate: string;
  flatInterestRate: string;
  annualInterestRate: string;
  interestCharges: string;
  applicationFee: string;
  totalAmountPayable: string;
  premiumTotal: string;
  depositAmount: string;
}

export interface EOfferBankDetails {
  accountName: string;
  bank: string;
  accountNumber: string;
}

export interface EOfferAcceptance {
  paymentFrequency: PaymentFrequency;
  paymentDate: string;
  bankDetails?: EOfferBankDetails;
  declarationsAccepted: boolean[];
  acceptedAt: string;
  ipAddress?: string;
}

export interface EOfferSnapshot {
  quoteId: string;
  quoteReference: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  clientAddress: string;
  risks: Risk[];
  totalPremiumExGst: number;
  totalPremiumIncGst: number;
  adminFee: number;
  brokerFee: number;
  instalmentOptions: {
    fortnightly: InstalmentDetails;
    monthly: InstalmentDetails;
    annual: { totalAmount: number };
  };
  createdAt: string;
  expiresAt: string;
}

export interface EOffer {
  id: string;
  token: string; // Unique tokenized link
  quoteId: string;
  status: EOfferStatus;
  snapshot: EOfferSnapshot;
  sentAt?: string;
  sentBy?: string;
  viewedAt?: string;
  acceptance?: EOfferAcceptance;
  declinedAt?: string;
  declineReason?: string;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface Quote {
  id: string;
  clientId: string;
  clientName: string;
  andoQuoteId?: string;
  type: QuoteType;
  status: QuoteStatus;
  premium: number;
  sumInsured?: number;
  createdAt: string;
  updatedAt: string;
  assignedAdviser: string;
  riskDetails: any;
  coverageOptions: any;
  referralSentDate?: string;
  referralApprovedDate?: string;
  referralNotes?: string;
  risks?: Risk[];
  totalChargeInclGst?: number;
  commissions?: Commission[];
  totalCommission?: number;
  policyStartDate?: string;
  policyExpiryDate?: string;
  autoRenew?: boolean;
  renewalStatus?: RenewalStatus;
  pricing?: QuotePricing;
  referrerNotes?: string;
  insurerNotes?: string;
  uploadedDocuments?: { id: string; name: string; uploadedAt: string }[];
  cancelledAt?: string;
  cancelledBy?: string;
  cancelReason?: string;
  eOfferSent?: boolean;
  eOfferSentDate?: string;
  eOfferAccepted?: boolean;
  quoteClassification?: 'STANDARD' | 'RENEWAL' | 'MTA';
  mtaVersion?: number;
  eOfferBindFailed?: boolean;
}

export type PolicyStatus =
  | 'saved'
  | 'issued'
  | 'valid'
  | 'expired'
  | 'ended'
  | 'cancelled'
  | 'declined'
  | 'superseded'
  | 'referral_submitted'
  | 'referral-action-required'
  | 'quoted'
  | 'mtaquote'
  | 'mta-in-progress'
  | 'pending-mta'
  | 'cancellation-pending';
export type Insurer = 'Ando' | 'FMG' | 'NZI' | 'Tower' | 'Vero';
export type PolicyProduct = 'House' | 'Contents' | 'Vehicle' | 'Domestic Combined' | 'Commercial Combined';
export type MissingDocument = 'Policy' | 'Policy Endorsement' | 'Wording Document' | 'Certificate' | 'Insurer Declaration' | 'Closing Document' | 'Coverage Document' | 'GST Invoice';
export type ImportedStatus = 'Imported' | 'Manual Entry' | 'API Sync';

// Canonical API contract (generated from backend OpenAPI schema).
export type PolicyApi = PolicyResponseDto;

export interface PolicyRiskPremiumRating {
  annual_amount: number;
  original_flag?: string | number;
  transaction_amount: number;
  rating_section_code: string;
  rating_section_description: string;
}

export interface PolicyRiskPremium {
  risk_key?: number | null;
  risk_id?: string;
  risk_type?: string;
  risk_description?: string;
  premiums?: {
    ratings?: PolicyRiskPremiumRating[];
    premium_due_net?: number;
    premium_due_tax?: number;
    premium_due_gross?: number;
  };
}

export interface PolicyPremiumFundingDetails {
  id: string;
  paymentFrequency: 'FORTNIGHTLY' | 'MONTHLY' | 'ANNUAL' | 'WEEKLY';
  policyAmount: number;
  policyIssueDate: string;
  policyStartDate: string;
  paymentStartDate: string;
  firstInstalment?: number | null;
  firstPaymentDueDate?: string | null;
  numberOfPayments?: number | null;
  regularInstalment?: number | null;
  regularInstalmentLastDate?: string | null;
  flatInterestRate?: number | null;
  annualInterestRate?: number | null;
  interestCharges?: number | null;
  applicationFee?: number | null;
  totalAmountPayable?: number | null;
  premiumTotal?: number | null;
  depositAmount?: number | null;
}

// Legacy UI policy projection. Prefer `PolicyApi` for new data-access code and
// only use these display aliases in view components.
export interface Policy {
  id: string;
  createdAt?: string;
  quoteId: string;
  andoQuoteId?: string;
  clientId: string;
  // Canonical API fields
  client?: PolicyApi['client'];
  policyType?: PolicyApi['policyType'];
  assignedAdviserId?: string;
  servicingAdviserId?: string;
  servicingAdviserName?: string;
  originalServicingAdviserId?: string;
  products?: Record<string, unknown> | null;
  header?: Record<string, any> | null;
  replacedByMTAId?: string;
  apiStatus?: PolicyApi['status'];
  apiPaymentFrequency?: PolicyApi['paymentFrequency'];
  apiPaymentStatus?: PolicyApi['paymentStatus'];
  mta?: Record<string, unknown> | null;

  // Legacy display aliases
  clientName: string;
  type: QuoteType;
  status: PolicyStatus;
  policyNetTotal?: number;
  adminFee?: number;
  brokerFee?: number;
  total?: number;
  totalPremium?: number;
  originalPolicyTotalPremium?: number;
  mtaGrossAnnualPremium?: number;
  currentGrossAnnualPremium?: number;
  totalNetPremium?: number;
  totalTax?: number;
  policyTotalTax?: number;
  riskPremiums?: PolicyRiskPremium[];
  premiumFundingDetails?: PolicyPremiumFundingDetails | null;
  totalCommission?: number;
  totalTaxOnCommission?: number;
  policyCommission?: PolicyApi['policyCommission'];
  startDate: string;
  expiryDate: string;
  assignedAdviser: string;
  assignedAdviserName: string;
  insurer: Insurer;
  product: PolicyProduct;
  objects: RiskType[];
  autoIssued: boolean;
  missingDocuments?: MissingDocument[];
  importedStatus: ImportedStatus;
  paymentFrequency: 'annual' | 'monthly' | 'fortnightly';
  paymentStatus: 'not paid' | 'paid' | 'overdue';
  enforcedDate?: string;
  renewalDate?: string;
  risks?: PolicyRisk[];
  claims?: PolicyClaim[];
  cancellationData?: PolicyCancellation;
  cancellationMetadata?: Record<string, any> | null;
  mtaHistory?: MTA[];
  // MTA-specific fields
  isMTA?: boolean;
  originalPolicyId?: string;
  mtaVersion?: number; // 1, 2, 3, etc.
  replacedByMTA?: string; // ID of the MTA policy that superseded this one
}

export interface ManualCancellationRiskValues {
  policyRiskId: string;
  totalCharge: number;
  cp: number;
  fsl: number;
  nhi: number;
  gst: number;
  commissionPercent: number;
  commissionPercentSource: 'ANDO' | 'MANUAL';
  commission: number;
  commissionGst: number;
  commissionTotal: number;
}

export interface PolicyCancellation {
  cancelledDate: string;
  effectiveCancellationDate: string;
  reason: string;
  reasonDetails?: string;
  calculationMethod?: 'pro-rata' | 'short-rate';
  adminFee: number;
  brokerFee: number;
  insurerCancellationFee: number;
  commissionClawback: number;
  totalFees: number;
  feesTax: number;
  /** Ando premium GST from cancellationMetadata.amounts.totalTax */
  premiumTax?: number;
  andoTotalGrossPremium?: number;
  andoTotalNetPremium?: number;
  earnedPremium?: number;
  unearnedPremium?: number;
  paymentsMade?: number;
  netSettlement: number;
  settlementType: 'refund' | 'outstanding' | 'nil';
  notes?: string;
  cancelledBy?: string;
  /** True when staff recorded the cancellation by hand instead of Ando calculating it */
  isManual?: boolean;
  /** Per-risk values entered (and commission calculated) on a manual cancellation */
  manualRisks?: ManualCancellationRiskValues[];
  /** MTAs reversed because the cancellation was dated on or before their effective date */
  reversedMtas?: Array<{ policyId: string; mtaVersion: number | null; mtaEffectiveDate: string | null }>;
  andoResponse?: Record<string, unknown>;
  refundAdminFees?: boolean;
  adminFeeRefundExclGst?: number;
  adminFeeRefundTax?: number;
  adminFeeRefundInclGst?: number;
}

export interface PolicyRisk {
  id: string;
  type: RiskType;
  description: string;
  sumInsured: number;
  excess: number;
  address?: string;
  offering?: string;
  andoRiskKey?: number;
  andoRiskStatus?: string;
  andoReferralDetails?: {
    referral_status?: string;
    referral_outcome?: string;
    referral_task_id?: string;
    referral_notes?: Array<{ note?: string; noteDateDisplay?: string }>;
    [key: string]: unknown;
  };
  andoReferralReasonsSnapshot?: unknown[] | null;
  originalFrontendRiskDetails?: Record<string, any>;
  /** Commission % Ando quoted for the risk (agent_comm); undefined when unknown */
  agentCommissionPercent?: number;
  createdAt?: string;
  updatedAt?: string;
  specificDetails?: {
    make?: string;
    model?: string;
    year?: string;
    registrationNumber?: string;
    buildingType?: string;
    bedrooms?: number;
    terms?: {
      excesses?: Array<{
        total_excess?: number | string;
        imposed_excess?: number | string;
        excess_under_25?: number | string;
        selected_excess?: string;
      }>;
      clauses?: Array<{
        clause_code?: string;
        clause_text?: string;
      }>;
      sublimits?: Array<{
        index?: string;
        limit?: string;
        caption?: string;
      }>;
      risk_premiums?: {
        annual_amt?: number | string;
        transaction_amt?: number | string;
        gst_ovr?: number | string;
        premium_due_net?: number | string;
        premium_due_tax?: number | string;
        premium_due_gross?: number | string;
        ratings?: Array<{
          annual_amount?: number | string;
          transaction_amount?: number | string;
          rating_section_code?: string;
          rating_section_description?: string;
        }>;
      };
    };
    specifiedItems?: Array<{
      description: string;
      value: number;
    }>;
  };
}

export interface PolicyClaim {
  id: string;
  claimDate: string;
  claimType: string;
  description: string;
  claimAmount: number;
  status: 'pending' | 'approved' | 'settled' | 'declined';
  settlementAmount?: number;
  settlementDate?: string;
}

// Mid-Term Adjustment (MTA) types
export type MTAStatus = 'pending-approval' | 'approved' | 'rejected' | 'expired';
export type MTAChangeType = 'add-risk' | 'remove-risk' | 'modify-risk' | 'coverage-change' | 'multiple-changes';

export interface MTARiskChange {
  id: string;
  changeType: 'add' | 'remove' | 'modify';
  riskType: RiskType;
  description: string;
  // Original values (for modify/remove)
  originalSumInsured?: number;
  originalExcess?: number;
  originalPremium?: number;
  // New values (for add/modify)
  newSumInsured?: number;
  newExcess?: number;
  newExcessValue?: number;
  newPremium?: number;
  originalExcessValue?: number;
  // Full risk details
  riskData?: Partial<PolicyRisk>;
  changeReason?: string;
  // Field-level diff for modify changes
  fieldChanges?: Array<{ label: string; from: string; to: string }>;
}

export interface MTAApproval {
  approvedBy: 'client' | 'adviser';
  approvedByName: string;
  approvedAt: string;
  approvalMethod: 'email-link' | 'portal' | 'adviser-confirmed' | 'phone';
  ipAddress?: string;
}

export interface MTA {
  id: string;
  policyId: string;
  mtaNumber: string;
  status: MTAStatus;
  changeType: MTAChangeType;

  // Dates
  requestedDate: string;
  effectiveDate: string;
  approvalExpiryDate: string;

  // Changes
  riskChanges: MTARiskChange[];
  coverageChanges?: string[];

  // Premium Calculations
  originalPremium: number;
  newPremium: number;
  premiumDifference: number; // positive = additional charge, negative = refund

  // Remaining term calculations
  remainingDays: number;
  remainingMonths: number;
  proratedOriginalPremium: number;
  proratedNewPremium: number;
  proratedDifference: number;

  // Payment impact
  paymentFrequency: PaymentFrequency;
  paymentImpact: {
    oneOffCharge?: number;
    oneOffRefund?: number;
    newMonthlyAmount?: number;
    newFortnightlyAmount?: number;
    originalMonthlyAmount?: number;
    originalFortnightlyAmount?: number;
    remainingPayments?: number;
  };

  // Settlement
  settlementMethod?: 'one-off-payment' | 'adjust-instalments' | 'refund' | 'credit-note' | 'no-settlement';
  settlementAmount?: number;
  settlementStatus?: 'pending' | 'completed' | 'refunded';

  // Approval
  approval?: MTAApproval;
  rejectionReason?: string;
  rejectedAt?: string;

  // Metadata
  requestedBy: string;
  requestedByName: string;
  notes?: string;
  internalNotes?: string;

  // Documents
  proposalDocumentUrl?: string;
  updatedScheduleUrl?: string;

  // Insurer
  insurerNotified: boolean;
  insurerReference?: string;
  insurerApprovalRequired: boolean;
  insurerApprovalStatus?: 'pending' | 'approved' | 'declined';

  createdAt: string;
  updatedAt: string;
}

export type AssetType = 'house' | 'contents' | 'vehicle';
export type AssetStatus = 'insured' | 'not-insured' | 'removed';

export interface Asset {
  id: string;
  clientId: string;
  type: AssetType;
  description: string;
  value: number;
  insured: boolean;
  status: AssetStatus;
  createdAt: string;
  updatedAt: string;
  // Risk-specific data matching the quote forms
  data: HouseRiskData | ContentsRiskData | VehicleRiskData | any;
}

export interface DashboardMetrics {
  activeQuotes: {
    total: number;
    inProgress: number;
    bound: number;
    referred: number;
    renewalDue: number;
  };
  clients: {
    total: number;
    inProgress: number;
    bound: number;
  };
  referrals: {
    total: number;
    approved: number;
    inProgress: number;
  };
  renewalsDue: number;
  lapsedPolicies: number;
}

export interface Activity {
  id: string;
  type: 'quote-created' | 'quote-bound' | 'policy-renewed' | 'referral-submitted';
  description: string;
  timestamp: string;
  clientName: string;
  adviserId: string;
}

// Renewal types
export type RenewalType = 'automatic' | 'manual';
export type RenewalWorkflowStatus =
  | 'pending'
  | 'awaiting-client-response'
  | 'confirmed-no-change'
  | 'request-callback'
  | 'pending-amendments'
  | 'awaiting-updated-pricing'
  | 'updated-terms-received'
  | 'pricing-finalised'
  | 'sent-to-client'
  | 'awaiting-payment'
  | 'renewed-successfully'
  | 'lapsed'
  | 'lost'
  | 'insurer-review';

export interface RenewalStatusHistory {
  id: string;
  status: RenewalWorkflowStatus;
  timestamp: string;
  performedBy: 'system' | 'adviser' | 'client';
  userName?: string;
  notes?: string;
}

export interface RenewalRisk {
  id: string;
  type: RiskType;
  description: string;
  currentSumInsured: number;
  newSumInsured: number;
  currentExcess: number;
  newExcess: number;
  currentPremium: number;
  newPremium: number;
  changes?: string[];
}

export interface RenewalWorkflowStep {
  status: RenewalWorkflowStatus;
  label: string;
  description: string;
  completedAt: string | null;
  triggeredBy: string | null;
}

export type SmsDeliveryStatus =
  | 'ENROUTE'
  | 'SUBMITTED'
  | 'DELIVERED'
  | 'EXPIRED'
  | 'REJECTED'
  | 'FAILED';

export interface RenewalNotification {
  id: string;
  type: string;
  sentAt: string;
  status: string;
  errorMsg?: string | null;
  deliveryStatus?: SmsDeliveryStatus | null;
  deliveryStatusCode?: string | null;
  deliveryUpdatedAt?: string | null;
  providerMessageId?: string | null;
}

export interface RenewalLostMeta {
  reason?: string;
  notes?: string;
  insurer?: string;
  policyNumber?: string;
}

export interface Renewal {
  expiryDate?: string; // Legacy/Alias support
  id: string;
  policyId: string;
  clientId: string;
  clientName: string;
  renewalNumber: string;
  renewalType: RenewalType; // 'automatic' or 'manual'
  paymentFrequency: PaymentFrequency;
  paymentMethod?: PaymentMethod | null;

  //Policy/
  policyReference: string;

  // Dates
  currentExpiryDate: string;
  renewalDate: string;
  workflowStartDate: string; // 60 days before renewal

  // Current Policy
  currentPolicyNumber: string;
  currentPremium: number;
  currentRisks: RenewalRisk[];

  // Renewal Policy
  newPremium: number;
  premiumDifference: number;
  premiumDifferencePercent: number;
  newRisks: RenewalRisk[];
  newPolicySchedule?: string; // Document URL

  // Amendment tracking
  hasAmendments?: boolean;
  amendedRisks?: Risk[]; // Full risk data for amendments
  amendedPremium?: number;
  amendedPremiumDifference?: number;
  autoGeneratedRisks?: RenewalRisk[]; // Original auto-generated terms
  autoGeneratedPremium?: number;

  // Status & Workflow
  status: RenewalWorkflowStatus;
  statusHistory: RenewalStatusHistory[];

  // Payment (for manual renewals)
  invoiceId?: string;
  invoiceAmount?: number;
  paymentDueDate?: string;

  // Insurer
  insurer: Insurer;
  insurerReferralRequired?: boolean;
  insurerReferralNotes?: string;

  // Assignment
  assignedAdviser: string;
  assignedAdviserName: string;
  assignedTo?: string; // User ID of the person handling this renewal (FAP/Adviser)

  // Metadata
  createdAt: string;
  updatedAt: string;

  /** Linked combined quote for modify-to-quote flow */
  renewalQuoteId?: string | null;
  renewalQuoteStatus?: string | null;

  /** Policy created when the renewal completed successfully */
  renewedPolicyId?: string | null;
  renewedPolicyReference?: string | null;
  renewedPolicyStartDate?: string | null;

  /** When an adviser disabled auto-renewal; null means it is still automatic */
  autoRenewalDisabledAt?: string | null;

  /** Comma-separated decision reason codes from the API */
  renewalDecisionReason?: string | null;

  /** When the e-offer was completed; stops follow-up comms */
  eOfferCompletedAt?: string | null;

  /** Scheduled notifications sent for this renewal */
  notifications?: RenewalNotification[];

  /** Ordered workflow timeline from the API */
  workflowSteps?: RenewalWorkflowStep[];

  /** Loss details when workflow status is lost */
  meta?: RenewalLostMeta | null;

  /** When the renewal lapsed or was marked lost */
  lapsedAt?: string | null;
}

// Invoicing types
export type InvoiceStatus = 'draft' | 'approved' | 'awaiting-payment' | 'part-paid' | 'overdue' | 'failed' | 'paid' | 'refunded' | 'written-off' | 'void';
export type InvoiceType = 'annual' | 'instalment' | 'adjustment' | 'renewal' | 'mta';
export type PaymentMethod = 'direct-debit' | 'credit-card' | 'bank-transfer' | 'premium-funding' | 'other';
export type PaymentStatus = 'success' | 'failed' | 'reversed' | 'refunded' | 'pending';
export type ScheduleStatus = 'active' | 'paused' | 'failed' | 'completed' | 'cancelled';
export type MandateStatus = 'active' | 'expired' | 'revoked';
export type ScheduleFrequency = 'weekly' | 'fortnightly' | 'monthly';
export type PaymentType = 'funded' | 'annual';

export interface Invoice {
  id: string;
  invoiceNumber?: string;
  invoiceSequence?: number;
  clientId: string;
  clientName: string;
  policyId: string;
  andoPolicyId?: string;
  policyMtaVersion?: number | null;
  policyProduct: PolicyProduct;
  productSnapshot?: {
    products: Array<{
      product_name: string;
      product_price: number;
    }>;
    adminFee: number;
    feeBreakdown?: Array<{
      description: string;
      amount: number;
    }>;
    transactionFee?: number;
    fee?: number;
    /** Free-text note, e.g. flagging a manual cancellation */
    note?: string;
  };
  premiumFundingDetails?: {
    id: string;
    paymentFrequency: 'FORTNIGHTLY' | 'MONTHLY' | 'ANNUAL';
    policyAmount: number;
    policyIssueDate: string;
    policyStartDate: string;
    paymentStartDate: string;
    firstInstalment?: number | null;
    firstPaymentDueDate?: string | null;
    numberOfPayments?: number | null;
    regularInstalment?: number | null;
    regularInstalmentLastDate?: string | null;
    flatInterestRate?: number | null;
    annualInterestRate?: number | null;
    interestCharges?: number | null;
    applicationFee?: number | null;
    totalAmountPayable?: number | null;
    premiumTotal?: number | null;
    depositAmount?: number | null;
  };
  issueDate: string;
  dueDate: string;
  type: InvoiceType;
  amount: number;
  policyNetTotal?: number;
  policyTotalTax?: number;
  paid: number;
  outstanding: number;
  invoicePaid?: boolean;
  invoicePaidAt?: string | null;
  commissionPaid?: boolean;
  commissionPaidAt?: string | null;
  insurerPaid?: boolean;
  insurerPaidAt?: string | null;
  status: InvoiceStatus;
  paymentMethod: PaymentMethod;
  paymentType: PaymentType;
  insurer: Insurer;
  assignedAdviser: string;
  assignedAdviserName?: string;
  company?: {
    name: string;
    addressLine1: string;
    addressLine2: string;
    phone: string;
    email: string;
    gstRegistration: string;
    accountName: string;
    accountNumber: string;
  };
}

export interface UnallocatedCash {
  id: string;
  amount: number;
  receivedDate: string;
  policyNumber?: string;
  clientId?: string;
  clientName?: string;
  notes?: string;
}

export type TransactionType = 'INV' | 'CSH' | 'R/P' | 'CR' | 'ADJ';
export type TransactionStatus = 'allocated' | 'unallocated' | 'reconciled';

export interface Transaction {
  id: string;
  clientId: string;
  clientName: string;
  branch: string;
  authRepAndBroker: string;
  salesTeam: string;
  serviceTeam: string;
  insurer: string;
  transactionId: string;
  transactionType: TransactionType;
  transactionDate: string;
  particulars: string;
  dateReceivedFromInsurer?: string;
  clientBalance: number;
  balance: number;
  policyId?: string;
  isAllocated: boolean;
  status: TransactionStatus;
}

export interface Payment {
  id: string;
  clientId: string;
  clientName: string;
  policyId: string;
  datetime: string;
  method: PaymentMethod;
  cardLast4?: string;
  ddReference?: string;
  amount: number;
  status: PaymentStatus;
  appliedTo: string; // Invoice ID or 'Unallocated'
  allocations?: { invoiceId: string; amount: number }[];
  gatewayResponse?: string;
  failureReason?: string;
}

export interface Schedule {
  id: string;
  policyId: string;
  policyProduct: PolicyProduct;
  clientId: string;
  clientName: string;
  frequency: ScheduleFrequency;
  instalments: number;
  startDate: string;
  endDate: string;
  nextDebit: string;
  collected: number;
  total: number;
  method: PaymentMethod;
  mandateStatus: MandateStatus;
  status: ScheduleStatus;
  arrears: number;
  assignedAdviser: string;
}

// User Management types
export type AdviserRole = 'AUTHORISED_REP' | 'QUOTING_ADVISER' | 'INTERNAL_ADVISER';
export type AdviserPermissionRole = 'adviser' | 'admin' | 'refer' | 'quote' | 'bind' | 'manager';
export type CommissionRecipient = 'adviser' | 'fap';

export interface BankingDetails {
  accountName: string;
  accountNumber: string;
  referenceFormat?: string;
}

export interface Document {
  id: string;
  name: string;
  type: string;
  uploadedAt: string;
  url?: string;
}

export interface CommissionSchedule {
  id: string;
  scope: 'fap' | 'adviser' | 'product' | 'insurer';
  fapId?: string;
  adviserId?: string;
  productType?: string;
  insurer?: Insurer;
  newBusinessPercent: number;
  renewalPercent: number;
  flatFeeNB?: number;
  flatFeeRN?: number;
  effectiveFrom: string;
  effectiveTo?: string;
  priority: number; // Lower number = higher priority
  createdAt: string;
  updatedAt: string;
}

export interface FAP {
  id: string;
  legalName: string;
  tradingName?: string;
  fapLicenceNumber: string;
  fsprNumber: string;
  gstNumber?: string;
  commissionPercentage?: number;
  overridePercentage?: number;
  overrideName?: string;
  brokerFeePercentage?: number;
  qaCommissionPercentage?: number;
  qaRenewalCommissionPercentage?: number;
  primaryContact: {
    name: string;
    email: string;
    phone: string;
  };
  registeredAddress: {
    street: string;
    suburb: string;
    city: string;
    postcode: string;
    country: string;
  };
  postalAddress?: {
    street: string;
    suburb: string;
    city: string;
    postcode: string;
    country: string;
  };
  banking: BankingDetails;
  documents: Document[];
  defaultCommissionScheduleId?: string;
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
  notes?: string;
}

export interface Adviser {
  id: string;  // User ID (for routing/navigation)
  adviserId?: string;  // Adviser entity ID (for matching with adviserLinks)
  fapId: string;
  fapName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  mobile?: string;
  adviserRole: AdviserRole | null;
  address?: {
    street: string;
    suburb: string;
    city: string;
    postcode: string;
    country: string;
  };
  fsprNumber?: string;
  roles: AdviserPermissionRole[];
  canRefer: boolean;
  canQuote: boolean;
  canBind: boolean;
  isManager: boolean;
  commissionRecipient: CommissionRecipient;
  hasCommissionOverride: boolean;
  commissionPercentage?: number;
  overridePercentage?: number;
  overrideName?: string;
  brokerFeePercentage?: number;
  qaCommissionPercentage?: number;
  qaRenewalCommissionPercentage?: number;
  commissionScheduleId?: string;
  status: 'active' | 'inactive';
  documents: Document[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
  lastLogin?: string;
}

export type SystemUserRole = UserRole;

export interface SystemUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  department?: string;
  fapId?: string; // FAP association
  fapName?: string;
  linkedAdviserId?: string; // Linked adviser record
  linkedAdviserName?: string;
  roles: SystemUserRole[];
  mfaEnabled: boolean;
  status: 'active' | 'inactive';
  lastLogin?: string;
  ipHistory: {
    ip: string;
    location: string;
    loginCount: number;
    lastLogin: string;
  }[];
  activityLog: {
    id: string;
    date: string;
    action: string;
    entity: string;
    reference: string;
  }[];
  linkedEntities: {
    type: 'adviser' | 'fap';
    id: string;
    name: string;
    role: string;
  }[];
  notes?: string;
  auditTrail: {
    id: string;
    date: string;
    action: string;
    performedBy: string;
    details: string;
  }[];
  createdAt: string;
  updatedAt: string;
}